import { useCallback, useState } from "react";
import { importLeadRows, loadPublicGoogleSheet } from "./leadImportService";
import { parseLeadFile, parsePastedRows, suggestLeadMapping } from "./leadImportUtils";

const EMPTY_DATA = { columns: [], rows: [], totalRows: 0, truncated: false };

const readError = (error, fallback) =>
  error?.response?.data?.message ||
  Object.values(error?.response?.data?.errors || {})[0]?.[0] ||
  error?.message ||
  fallback;

export default function useLeadImport({ workspaceId, pipelineId, customFields = [], starterFields }) {
  const [step, setStep] = useState("source");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(EMPTY_DATA);
  const [sourceLabel, setSourceLabel] = useState("");
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const acceptParsed = useCallback((parsed, label, suggestedMapping) => {
    const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
    const columns = Array.isArray(parsed?.columns) ? parsed.columns : [];

    if (!columns.length || !rows.length) {
      throw new Error("Add a header row and at least one lead.");
    }

    setData({
      columns,
      rows,
      totalRows: parsed.totalRows ?? parsed.total_rows ?? rows.length,
      truncated: Boolean(parsed.truncated),
    });
    setSourceLabel(label);
    const suggestions = suggestedMapping && Object.keys(suggestedMapping).length
      ? suggestedMapping
      : suggestLeadMapping(columns, customFields);
    const customSuggestions = suggestLeadMapping(columns, customFields);
    const enabled = starterFields ? new Set(["name", ...starterFields.map((field) => field.key)]) : null;
    setMapping(Object.fromEntries(Object.entries({ ...customSuggestions, ...suggestions }).filter(([key]) => !enabled || key.startsWith("custom:") || enabled.has(key))));
    setError("");
    setStep("map");
  }, [customFields, starterFields]);

  const loadFile = useCallback(async (file) => {
    setLoading(true);
    setError("");
    try {
      acceptParsed(await parseLeadFile(file), file.name);
    } catch (loadError) {
      setError(readError(loadError, "We couldn't read that file."));
    } finally {
      setLoading(false);
    }
  }, [acceptParsed]);

  const loadPaste = useCallback((text) => {
    setError("");
    try {
      acceptParsed(parsePastedRows(text), "Pasted spreadsheet rows");
    } catch (loadError) {
      setError(readError(loadError, "We couldn't read the pasted rows."));
    }
  }, [acceptParsed]);

  const loadGoogleSheet = useCallback(async (url) => {
    if (!workspaceId) {
      setError("Select a workspace first.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await loadPublicGoogleSheet(workspaceId, url.trim());
      const payload = response?.data?.data || {};
      acceptParsed(payload, "Public Google Sheet", payload.suggested_mapping);
    } catch (loadError) {
      setError(readError(loadError, "We couldn't load that Google Sheet."));
    } finally {
      setLoading(false);
    }
  }, [acceptParsed, workspaceId]);

  const setFieldMapping = useCallback((field, column) => {
    setMapping((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        if (next[key] === column) delete next[key];
      });
      if (column) next[field] = column;
      else delete next[field];
      return next;
    });
  }, []);

  const runImport = useCallback(async () => {
    if (!workspaceId) {
      setError("Select a workspace before importing.");
      return null;
    }
    if (!mapping.name) {
      setError("Map one column to Lead name before importing.");
      return null;
    }

    setImporting(true);
    setError("");
    try {
      const response = await importLeadRows(workspaceId, {
        pipeline_id: pipelineId || null,
        mapping,
        rows: data.rows,
      });
      const summary = response?.data?.data || null;
      setResult(summary);
      setStep("result");
      return summary;
    } catch (importError) {
      setError(readError(importError, "The lead import could not be completed."));
      return null;
    } finally {
      setImporting(false);
    }
  }, [data.rows, mapping, pipelineId, workspaceId]);

  return {
    step,
    setStep,
    loading,
    importing,
    error,
    columns: data.columns,
    rows: data.rows,
    totalRows: data.totalRows,
    truncated: data.truncated,
    sourceLabel,
    mapping,
    result,
    loadFile,
    loadPaste,
    loadGoogleSheet,
    setFieldMapping,
    runImport,
  };
}
